"use client";
import React, { useRef, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { X, Mail, Lock, User, Phone, Key, ArrowLeft } from "lucide-react";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  name?: string;
  phoneNo?: string;
  general?: string;
}

export default function AuthModal({
  isOpen,
  onClose,
  onLoginSuccess,
}: AuthModalProps) {
  const [mode, setMode] = useState<"login" | "signup" | "forgot" | "reset">(
    "login"
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const modalRef = useRef<HTMLDivElement>(null);

  // Form states
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({
    name: "",
    phoneNo: "",
    email: "",
    password: "",
  });
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetData, setResetData] = useState({
    code: "",
    password: "",
    confirm: "",
  });

  // Clear form fields function
  const clearFormFields = () => {
    setLoginData({ email: "", password: "" });
    setSignupData({
      name: "",
      phoneNo: "",
      email: "",
      password: "",
    });
    setForgotEmail("");
    setResetData({
      code: "",
      password: "",
      confirm: "",
    });
  };

  useEffect(() => {
    setErrors({});
    setTouched({});
  }, [mode]);

  useEffect(() => {
    if (!isOpen) {
      // Clear form fields when modal closes
      clearFormFields();
    }
  }, [isOpen]);

  // ------------------- Handlers -------------------
  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setLoginData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSignupChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSignupData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // console.log("calling");
      // console.log(loginData);

      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(loginData),
      });
      // console.log(res)
      const result: any = await res.json();

      if (result?.success) {
        localStorage.setItem("userLoggedIn", "true");

        toast.success("Logged in successfully!");

        // Clear login form fields
        setLoginData({ email: "", password: "" });
        
        // ✅ CLOSE MODAL + REDIRECT (via Navbar)
        onLoginSuccess();
        onClose();
      }
      else {
        const errorMsg =
          result?.message?.error?.message ||
          result?.message ||
          "Login failed. Please check your credentials.";
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error("AuthModal login error:", err);
      toast.error("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // console.log("Signing up with data:", signupData);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(signupData),
      });

      const result: any = await res.json();

      if (result?.success) {
        localStorage.setItem("userLoggedIn", "true");
        localStorage.setItem("isFirstOrder", "true");
        toast.success("Account created!");
        
        // Clear signup form fields
        setSignupData({
          name: "",
          phoneNo: "",
          email: "",
          password: "",
        });
        
        // Switch to login mode with clean form
        setMode("login");
        onLoginSuccess();
        onClose();
      } else {
        const errorMsg =
          result?.message?.error?.message ||
          result?.message ||
          "Signup failed. Please try again.";

        if (errorMsg === "User already exist") {
          toast.error(
            "An account with this email already exists. Please log in instead."
          );
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Unexpected error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return toast.error("Email is required");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const result: any = await res.json();

      if (result.success) {
        toast.success(result.message);
        // Clear forgot password field
        setForgotEmail("");
        // Switch to reset mode
        setMode("reset");
      } else toast.error(result.message || "Request failed");
    } catch {
      toast.error("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetData.password || resetData.password !== resetData.confirm) {
      return toast.error("Passwords must match");
    }
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: resetData.code,
          password: resetData.password,
          passwordConfirmation: resetData.confirm,
        }),
      });
      const result: any = await res.json();
      if (result.success) {
        toast.success(result.message || "Password reset successful!");
        // Clear reset form fields
        setResetData({
          code: "",
          password: "",
          confirm: "",
        });
        setMode("login");
      } else toast.error(result.message || "Reset failed");
    } catch {
      toast.error("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Variants -------------------
  const tabVariants = {
    active: { 
      backgroundColor: "#f97316", 
      color: "#ffffff",
      scale: 1.02 
    },
    inactive: { 
      backgroundColor: "#f3f4f6", 
      color: "#6b7280",
      scale: 1 
    },
  };
  
  const contentVariants = {
    enter: { x: -20, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: 20, opacity: 0 },
  };

  // ------------------- Return -------------------
  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel 
                ref={modalRef}
                className="w-full max-w-sm transform overflow-hidden rounded-xl bg-white p-0 text-left align-middle shadow-2xl border border-gray-200 transition-all"
              >
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 z-10 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>

                {/* Header with Tabs */}
                <div className="pt-6 pb-3 px-5">
                  <Dialog.Title
                    as="h2"
                    className="text-lg font-bold text-gray-900 text-center mb-1"
                  >
                    {mode === "login" && "Welcome Back"}
                    {mode === "signup" && "Create Account"}
                    {mode === "forgot" && "Reset Password"}
                    {mode === "reset" && "Set New Password"}
                  </Dialog.Title>
                  <Dialog.Description className="text-gray-600 text-center text-xs mb-4">
                    {mode === "login" && "Sign in to continue"}
                    {mode === "signup" && "Join us today"}
                    {mode === "forgot" && "Enter your email to reset password"}
                    {mode === "reset" && "Enter your code and new password"}
                  </Dialog.Description>

                  {/* Tabs */}
                  {mode !== "forgot" && mode !== "reset" && (
                    <div className="flex bg-gray-100 p-0.5 rounded-lg mb-4">
                      {["login", "signup"].map((tab) => (
                        <motion.button
                          key={tab}
                          variants={tabVariants as any}
                          initial={false}
                          animate={mode === tab ? "active" : "inactive"}
                          className="flex-1 py-2 font-medium text-xs uppercase tracking-wider rounded-md transition-all duration-200"
                          onClick={() => setMode(tab as any)}
                        >
                          {tab === "login" ? "Login" : "Sign Up"}
                        </motion.button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Animated Content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={mode}
                    variants={contentVariants as any}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.2 }}
                    className="px-5 pb-5"
                  >
                    {/* ✅ LOGIN */}
                    {mode === "login" && (
                      <form onSubmit={handleLogin} className="space-y-3">
                        <div className="space-y-2.5">
                          <div className="relative">
                            <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="email"
                              name="email"
                              placeholder="Email"
                              value={loginData.email}
                              onChange={handleLoginChange}
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="password"
                              name="password"
                              placeholder="Password"
                              value={loginData.password}
                              onChange={handleLoginChange}
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <span className="flex items-center justify-center gap-1.5">
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Logging in...
                            </span>
                          ) : (
                            "Sign In"
                          )}
                        </button>

                        <div className="text-center pt-1">
                          <button
                            type="button"
                            className="text-xs text-gray-600 hover:text-orange-600 transition-colors"
                            onClick={() => setMode("forgot")}
                          >
                            Forgot password?
                          </button>
                        </div>
                      </form>
                    )}

                    {/* ✅ SIGNUP */}
                    {mode === "signup" && (
                      <form onSubmit={handleSignup} className="space-y-3">
                        <div className="space-y-2">
                          <div className="relative">
                            <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              name="name"
                              placeholder="Full name"
                              value={signupData.name}
                              onChange={handleSignupChange}
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                          <div className="relative">
                            <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="email"
                              name="email"
                              placeholder="Email"
                              value={signupData.email}
                              onChange={handleSignupChange}
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                          <div className="relative">
                            <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="tel"
                              name="phoneNo"
                              placeholder="Phone"
                              value={signupData.phoneNo}
                              onChange={handleSignupChange}
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="password"
                              name="password"
                              placeholder="Password"
                              value={signupData.password}
                              onChange={handleSignupChange}
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                        </div>

                        <div className="text-[10px] text-gray-500 pt-1 px-1">
                          <p>Use 8+ characters with letters, numbers & symbols</p>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                        >
                          {loading ? (
                            <span className="flex items-center justify-center gap-1.5">
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Creating...
                            </span>
                          ) : (
                            "Create Account"
                          )}
                        </button>

                        <div className="text-[10px] text-gray-500 text-center pt-2 px-1">
                          <p>
                            By signing up, you agree to our{" "}
                            <button type="button" className="text-orange-600 hover:underline">
                              Terms
                            </button>{" "}
                            and{" "}
                            <button type="button" className="text-orange-600 hover:underline">
                              Privacy
                            </button>
                          </p>
                        </div>
                      </form>
                    )}

                    {/* ✅ FORGOT PASSWORD */}
                    {mode === "forgot" && (
                      <form onSubmit={handleForgot} className="space-y-3">
                        <div className="text-center mb-1">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Key className="w-5 h-5 text-orange-600" />
                          </div>
                          <p className="text-xs text-gray-600">
                            Enter your email to reset your password
                          </p>
                        </div>

                        <div className="relative">
                          <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="email"
                            placeholder="Email"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                          />
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2.5 rounded-lg text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <span className="flex items-center justify-center gap-1.5">
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Sending...
                            </span>
                          ) : (
                            "Send Reset Link"
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => setMode("login")}
                          className="w-full border border-gray-200 hover:border-gray-300 bg-white text-gray-700 text-sm font-medium py-2 rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                          Back to Login
                        </button>
                      </form>
                    )}

                    {/* ✅ RESET PASSWORD */}
                    {mode === "reset" && (
                      <form onSubmit={handleReset} className="space-y-3">
                        <div className="text-center mb-1">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Lock className="w-5 h-5 text-orange-600" />
                          </div>
                          <p className="text-xs text-gray-600">
                            Enter code and new password
                          </p>
                        </div>

                        <div className="space-y-2">
                          <div className="relative">
                            <Key className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Reset code"
                              value={resetData.code}
                              onChange={(e) =>
                                setResetData((prev) => ({
                                  ...prev,
                                  code: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="password"
                              placeholder="New password"
                              value={resetData.password}
                              onChange={(e) =>
                                setResetData((prev) => ({
                                  ...prev,
                                  password: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                          <div className="relative">
                            <Lock className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="password"
                              placeholder="Confirm password"
                              value={resetData.confirm}
                              onChange={(e) =>
                                setResetData((prev) => ({
                                  ...prev,
                                  confirm: e.target.value,
                                }))
                              }
                              className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 transition-all outline-none text-gray-900 placeholder-gray-500"
                            />
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-gray-800 hover:bg-black text-white font-medium py-2.5 rounded-lg text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <span className="flex items-center justify-center gap-1.5">
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Resetting...
                            </span>
                          ) : (
                            "Reset Password"
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => setMode("login")}
                          className="w-full border border-gray-200 hover:border-gray-300 bg-white text-gray-700 text-sm font-medium py-2 rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                          Back to Login
                        </button>
                      </form>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Footer */}
                {(mode === "login" || mode === "signup") && (
                  <div className="border-t border-gray-100 px-5 py-3 bg-gray-50">
                    <p className="text-center text-xs text-gray-600">
                      {mode === "login" ? "Don't have an account? " : "Already have an account? "}
                      <button
                        type="button"
                        onClick={() => setMode(mode === "login" ? "signup" : "login")}
                        className="text-orange-600 hover:text-orange-700 font-medium transition-colors"
                      >
                        {mode === "login" ? "Sign up" : "Sign in"}
                      </button>
                    </p>
                  </div>
                )}

                {/* ✅ Toast Container */}
                <ToastContainer
                  position="bottom-center"
                  autoClose={3000}
                  hideProgressBar={false}
                  closeOnClick
                  pauseOnHover={false}
                  draggable
                  className="!absolute !bottom-2 !left-1/2 !-translate-x-1/2 !w-[90%]"
                  toastClassName="!bg-white !text-gray-900 !rounded-lg !border !border-gray-200 !shadow-lg !p-3 !min-h-0 !m-0 !text-xs"
                  // bodyClassName="!p-0 !m-0"
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}